import { FunctionComponent, useState } from 'react';
import { useRouter } from 'next/router';

import Button, { ButtonStyles, ButtonTypes } from 'components/layout/Button';
import Form from 'components/layout/forms/Form';
import Input from 'components/layout/forms/Input';
import { Validation } from 'components/layout/forms/PasswordValidation';
import PasswordValidation from 'components/layout/forms/PasswordValidation/component';
import LayoutContainer, { Sizes } from 'components/layout/LayoutContainer';
import ReCAPTCHA from 'components/layout/ReCAPTCHA';

import StyledComponent from './styles';
import { Errors, InputTypes, PasswordVisibility, Props } from './types';

const PublicPagePasswordResetSetNewPassword: FunctionComponent<Props> = ({
    onSuccess,
    actions,
    localeData: { headline, form: { inputPassLabel, inputRepeatLabel, seePassButtonLabel, submitButtonLabel, validation } },
}) => {
    const [errorMessage, setErrorMessage]: [Errors, Function] = useState(null);
    const [passwordVisibility, setPasswordVisibility]: [PasswordVisibility, Function] = useState({
        password: false,
        passwordConfirm: false,
    });
    const [isPasswordValid, setIsPasswordValid]: [boolean, Function] = useState(false);
    const [recaptchaToken, setRecaptchaToken]: [string, Function] = useState(null);

    const handlePasswordChange = (passwordTests: Validation) => {
        if (!passwordTests) {
            return;
        }
        const notPassed = Object.values(passwordTests).filter(passwordTest => {
            return passwordTest === false;
        });
        if (notPassed.length > 0) {
            setIsPasswordValid(false);
        } else {
            setIsPasswordValid(true);
        }
    };
    const router = useRouter();
    const token = router.query?.token;

    return (
        <StyledComponent
            className="public-page-password-reset-set-new-password"
        >
            <LayoutContainer
                size={Sizes?.XMedium}
                className="content-container"
            >
                <div
                    className="headline"
                    dangerouslySetInnerHTML={{ __html: headline }}
                />

                <Form
                    submitAction={async (values) => {
                        try {
                            const response = await actions.passwordSet({
                                password: values?.password,
                                passwordConfirm: values?.passwordConfirm,
                                token,
                                recaptchaToken,
                            });
                            return response;
                        }
                        catch (error) {
                            console.error(error);
                            setErrorMessage(error?.payload?.originalError?.data?.errors);
                            throw error;
                        }
                    }}
                    onSuccess={onSuccess}
                >
                    {({ formik }) => (
                        <div className="form-container">
                            <div className="form-inputs">
                                <div className="input-container">
                                    <Input
                                        name='password'
                                        label={inputPassLabel}
                                        onChange={formik.handleChange}
                                        value={formik.values.password}
                                        type={passwordVisibility.password ? InputTypes.Text : InputTypes.Password}
                                        error={errorMessage?.password}
                                    />
                                    <Button
                                        className="button-display-password"
                                        type={ButtonTypes.Button}
                                        style={ButtonStyles.None}
                                        onClick={() => setPasswordVisibility({ ...passwordVisibility, password: !passwordVisibility.password })
                                        }
                                    >
                                        {seePassButtonLabel}
                                    </Button>
                                </div>
                                <div className="input-container">
                                    <Input
                                        name='passwordConfirm'
                                        label={inputRepeatLabel}
                                        onChange={formik.handleChange}
                                        value={formik.values.passwordConfirm}
                                        type={passwordVisibility.passwordConfirm ? InputTypes.Text : InputTypes.Password}
                                        error={errorMessage?.passwordConfirm}
                                    />
                                    <Button
                                        className="button-display-password"
                                        type={ButtonTypes.Button}
                                        style={ButtonStyles.None}
                                        onClick={() => setPasswordVisibility({ ...passwordVisibility, passwordConfirm: !passwordVisibility.passwordConfirm })
                                        }
                                    >
                                        {seePassButtonLabel}
                                    </Button>
                                </div>
                                <div className="input-container">
                                    <PasswordValidation
                                        translations={validation}
                                        password={formik.values.password}
                                        checkIfValid={handlePasswordChange}
                                    />
                                </div>
                            </div>

                            <div className="google-recaptcha">
                                <ReCAPTCHA
                                    onChange={(token: string) => setRecaptchaToken(token)}
                                    error={errorMessage?.recaptchaToken}
                                />
                            </div>

                            <Button
                                style={ButtonStyles.Primary}
                                className="submit-button"
                                loading={formik.isSubmitting}
                                type={ButtonTypes.Submit}
                                disabled={!(isPasswordValid && (formik.values.password === formik.values.passwordConfirm))}
                            >
                                {submitButtonLabel}
                            </Button>
                        </div>
                    )}
                </Form>

            </LayoutContainer>
        </StyledComponent>
    );
};

export default PublicPagePasswordResetSetNewPassword;